import { OidcClientService, createAllScopes } from '@xeovision/shared';
import {
  SigninRedirectArgs,
  User,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { toast } from 'vue-sonner';

// Refresh this many seconds before expiry so a token can't expire while a request is in flight.
const TOKEN_EXPIRY_MARGIN_S = 30;

export const useAuthClientStore = defineStore('auth-client-store', () => {
  const runtimeConfig = useRuntimeConfig();
  const authNStore = useAuthNStore();
  const router = useRouter();

  // urn:zitadel:iam:org:project:role:{rolekey} // ask for role in project
  const roleScopes = createAllScopes();
  // I was not able to return roles from mather organization:xeoLand, owner of project: xeoVision despite roles are set in ZITADEL
  //  no matter roles are forced by 'Assert Roles on Authentication' flag or scopes ask explicitly for roles
  const scopes = [
    'openid',
    'profile',
    'email',
    'offline_access', // for refresh token
    'urn:zitadel:iam:user:metadata', // get user metadata
    `urn:zitadel:iam:org:id:${runtimeConfig.public.ZITADEL_DEFAULT_AUTHN_ORGANIZATION_ID}`, // force xeoGeneric org login view - only first org is incldued

    ...roleScopes,
    // 'urn:zitadel:iam:org:project:id:zitadel:aud', // it gives ZITADEL project id in aud claim ~= instance_id. Useful when want validate by instance_id?
  ];

  const scope = scopes.join(' ');
  const settings: UserManagerSettings = {
    authority: runtimeConfig.public.ZITADEL_AUTHORITY,
    client_id: runtimeConfig.public.ZITADEL_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/login`,
    silent_redirect_uri: `${window.location.origin}/auth/silent-refresh`,
    post_logout_redirect_uri: `${window.location.origin}/auth/logout`,
    response_type: 'code',
    userStore: new WebStorageStateStore(),
    scope: scope,
    automaticSilentRenew: false,
    loadUserInfo: true,
  };

  const zitadel = new OidcClientService(settings);

  // Refresh tokens are single-use, so concurrent refreshes (expiry timer + in-flight requests) share one call.
  // On failure the session is gone: drop the user locally so requests continue anonymously instead of
  // carrying a stale token that the API rejects with 401.
  let refreshing: Promise<User | null> | null = null;
  function refreshUser(): Promise<User | null> {
    refreshing ??= (async () => {
      try {
        const user = await zitadel.signinSilent();
        if (!user) throw new Error('silent signin returned no user');
        authNStore.setUser(user);
        return user;
      } catch (error) {
        console.error('Token refresh error:', error);
        await zitadel.removeUser();
        authNStore.clearUser();
        return null;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  /** Access token to send with a request: refreshed when (almost) expired, null when logged out. */
  async function getValidAccessToken(): Promise<string | null> {
    let user = authNStore.user;
    if (user && (user.expires_in ?? Infinity) <= TOKEN_EXPIRY_MARGIN_S) user = await refreshUser();
    return user?.access_token ?? null;
  }

  const handleTokenRefresh = async () => {
    const user = await refreshUser();
    if (user) toast.success('You are still logged in!');
    else toast.error('Session expired... logged out');
  };

  zitadel.setAccessTokenExpiringCallback(handleTokenRefresh);

  zitadel.setAccessTokenExpiredCallback(handleTokenRefresh);

  async function statefulLogin() {
    const route = router.currentRoute.value;
    const signinRedirectArgs: SigninRedirectArgs = {
      state: {
        nextPath: route.path,
        nextQuery: route.query,
      },
    };
    return zitadel.login(signinRedirectArgs);
  }

  async function statefulLogout() {
    const route = router.currentRoute.value;
    const logoutState = {
      nextPath: route.path,
      nextQuery: route.query,
    };
    localStorage.setItem('xeo-logout-state', JSON.stringify(logoutState));
    return zitadel.logout();
  }

  async function handleRefreshProfile(): Promise<boolean> {
    try {
      const user = await zitadel.signinSilent();
      if (!user) {
        return false;
      }
      authNStore.setUser(user);
      toast.success('Profile refreshed');
      return true;
    } catch (error) {
      toast.error('Error refreshing profile');
      console.error('Error refreshing profile');
      return false;
    }
  }

  function openZitadelProfile() {
    const user = authNStore.user;
    if (!user) {
      return;
    }

    const profileUrl = `${runtimeConfig.public.ZITADEL_AUTHORITY}/ui/console/users/me`;
    window.open(profileUrl, '_blank');
  }

  return {
    zitadel,
    getValidAccessToken,
    statefulLogin,
    statefulLogout,
    handleRefreshProfile,
    openZitadelProfile,
  };
});

// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthClientStore, import.meta.hot));
}
